import { getFilesUrls } from '@/jobs/storage/getFileUrl'
import { uploadWithTus } from '@/jobs/storage/tus'
import { Button } from '@/presentation/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/presentation/components/dialog'
import {
  FileInput,
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
} from '@/presentation/components/fileUpload'
import { Progress } from '@/presentation/components/progress'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/presentation/components/tooltip'
import { toast } from '@/presentation/components/use-toast'
import { createClient } from '@/core/supabase/client'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { UploadIcon } from '@radix-ui/react-icons'
import mime from 'mime'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import type { AutoFormWidgetsProps } from '../autoForm'
import { FieldDescription } from '../description'
import { Field } from '../field'
import { FieldControl } from '../fieldControl'
import { FieldLabel } from '../label'
import { FieldMessage } from '../message'

interface FileWithProgress {
  file: File
  id: string
  progress: number
}

interface UploadedFile {
  url: string
  alt: string
  id: string
  path: string
}

function getStoragePathFromUrl(fileUrl: string): string {
  const url = new URL(fileUrl)
  const idx = url.pathname.indexOf('/app/')
  if (idx >= 0) {
    // retorna apenas a key dentro do bucket (ex: contents/uuid.png)
    return url.pathname.slice(idx + '/app/'.length)
  }
  return ''
}

const SortableItem = ({
  file,
  index,
  onRemove,
}: {
  file: UploadedFile | FileWithProgress
  index: number
  onRemove: (file: UploadedFile | FileWithProgress) => void
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: file.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const getFileName = useCallback((): string => {
    if ('file' in file) {
      return (file as FileWithProgress).file.name
    }

    // Extrair o nome do arquivo da URL ou usar o alt
    return (
      (file as UploadedFile).alt || (file as UploadedFile).url?.split('/').pop() || 'Carregando...'
    )
  }, [file])

  const getFileUrl = useCallback(() => {
    if ('url' in file) {
      const url = new URL(file.url as string)
      const type = mime.getType(url.pathname)
      const extension = mime.getExtension(type || '')

      if (extension && type?.startsWith('image/')) {
        return file.url as string
      }

      return '/file.svg'
    }

    // Para arquivos em upload, podemos criar uma URL temporária se for uma imagem
    return file.file.type?.startsWith('image/') ? URL.createObjectURL(file.file) : '/file.svg'
  }, [file])

  const src = getFileUrl()

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <FileUploaderItem
        index={index}
        className="size-20 p-0 rounded-md overflow-hidden shadow border bg-white mt-2 relative"
        onRemove={() => onRemove(file)}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Image
                src={src}
                alt={getFileName()}
                height={80}
                width={80}
                className="p-0 h-[80px] w-[80px]"
                unoptimized={src.startsWith('http')}
              />
            </TooltipTrigger>
            <TooltipContent>{getFileName()}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {'progress' in file && file.progress < 100 && (
          <div className="absolute bottom-0 left-0 right-0 bg-white/80 p-1">
            <Progress value={file.progress} className="h-1" />
          </div>
        )}
      </FileUploaderItem>
    </div>
  )
}

export function S3FileWidget(props: AutoFormWidgetsProps) {
  const [files, setFiles] = useState<FileWithProgress[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [fileToDelete, setFileToDelete] = useState<UploadedFile | FileWithProgress | null>(null)
  const [abortControllers] = useState<Map<string, AbortController>>(new Map())

  useEffect(() => {
    const initialPaths = Array.isArray(props.initialValue) ? (props.initialValue as string[]) : []
    if (initialPaths.length) {
      getFilesUrls(initialPaths).then((initialFiles) => {
        setUploadedFiles(
          initialPaths
            .map((path, idx) => ({
              id: crypto.randomUUID(),
              path,
              url: initialFiles[idx]?.url || '',
              alt: initialFiles[idx]?.alt || '',
            }))
            .filter((it) => !!it.url),
        )
      })
    }
  }, [props.initialValue])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 2,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const t = useTranslations()

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setUploadedFiles((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleFileChange = async (newFiles: File[] | null) => {
    if (!newFiles) return

    const filesWithProgress: FileWithProgress[] = newFiles.map((file) => ({
      file,
      id: crypto.randomUUID(),
      progress: 0,
    }))

    setFiles((prev) => [...prev, ...filesWithProgress])

    for (const file of filesWithProgress) {
      const controller = new AbortController()
      abortControllers.set(file.id, controller)

      try {
        const fullUrl = await uploadWithTus(
          file.file,
          (props.options?.directory as string) || 'contents',
          (progress) => {
            setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, progress } : f)))
          },
          controller.signal,
        )

        const path = getStoragePathFromUrl(fullUrl)
        const signed = path ? await getFilesUrls([path]) : []
        const previewUrl = signed[0]?.url || fullUrl

        setUploadedFiles((prev) => [
          ...prev,
          {
            id: file.id,
            path,
            url: previewUrl,
            alt: file.file.name,
          },
        ])
      } catch (error) {
        if (error instanceof Error && error.message === 'Upload cancelado') {
          console.log('Upload cancelado pelo usuário')
        } else {
          const errorMessage = error instanceof Error ? error.message : String(error)
          console.error('Erro no upload:', error)
          // Mostrar mensagem de erro mais amigável ao usuário
          const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
          if (!supabaseUrl) {
            alert(
              'Erro de configuração: NEXT_PUBLIC_SUPABASE_URL não está configurada. ' +
                'Configure as variáveis do Supabase na Vercel e faça um novo deploy.',
            )
            continue
          }

          if (
            errorMessage.includes('Supabase') ||
            errorMessage.includes('storage') ||
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('JWT') ||
            errorMessage.includes('token')
          ) {
            alert(
              'Erro no upload: não foi possível autenticar no Storage. ' +
                'Verifique se você está logado e se o bucket/políticas permitem upload.',
            )
          } else {
            alert(`Erro no upload: ${errorMessage}`)
          }
        }
      } finally {
        abortControllers.delete(file.id)
        setFiles((prev) => prev.filter((f) => f.id !== file.id))
      }
    }
  }

  const cancelUpload = (fileId: string) => {
    const controller = abortControllers.get(fileId)
    if (controller) {
      controller.abort()
      abortControllers.delete(fileId)
      setFiles((prev) => prev.filter((f) => f.id !== fileId))
    }
  }

  const handleRemoveFile = async (file: UploadedFile | FileWithProgress) => {
    if ('file' in file) {
      // Se for um arquivo em upload, cancela o upload
      cancelUpload(file.id)
    } else {
      // Se for um arquivo já uploadado, mostra o diálogo de confirmação
      setFileToDelete(file)
    }
  }

  const confirmDelete = async () => {
    if (!fileToDelete) return

    if ('url' in fileToDelete) {
      // Deleção via Supabase client (autenticado) — evita depender de chaves S3 públicas.
      try {
        const client = createClient()
        const path = fileToDelete.path

        if (path) {
          const { error } = await client.storage.from('app').remove([path])
          if (error) {
            throw error
          }
        }

        toast({
          title: 'Arquivo removido',
          description: 'Arquivo removido com sucesso',
          variant: 'default',
        })
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[S3FileWidget] Falha ao remover arquivo do Storage:', error)
        }
        toast({
          title: 'Erro ao remover arquivo',
          description: 'Não foi possível remover o arquivo',
          variant: 'destructive',
        })
      }

      setUploadedFiles((prev) => prev.filter((f) => f.id !== fileToDelete.id))
    } else {
      setFiles((prev) => prev.filter((f) => f.id !== fileToDelete.id))
    }

    setFileToDelete(null)
  }

  const uploadedPaths =
    uploadedFiles.map((it) => it.path).filter((it) => typeof it === 'string' && it.length > 0) || []

  return (
    <Field path={props.path} className={props.classNames?.root}>
      <select
        multiple
        className="hidden"
        name={props.path}
        value={uploadedPaths}
        onChange={() => {}}
      >
        {uploadedPaths.map((it) => (
          <option key={it} value={it} />
        ))}
      </select>
      <FieldLabel>{props.options?.label as string}</FieldLabel>
      <FieldDescription>{props.options?.description as string}</FieldDescription>
      <FieldControl>
        <FileUploader
          value={files.map((f) => f.file)}
          onValueChange={handleFileChange}
          className="relative bg-transparent rounded-sm py-2"
          dropzoneOptions={{
            multiple: !props.options?.single,
            maxFiles: 100,
            maxSize: 1024 * 1024 * 4,
          }}
        >
          <FileInput
            name={props.path}
            className="border-2 border-dashed shadow-sm border-input bg-white/50"
          >
            <div className="flex flex-col text-center items-center justify-center w-full py-4">
              <UploadIcon className="h-6 w-6" />
              <p className="mt-2 text-sm text-zinc-600">
                <span className="font-semibold">Arrastar arquivos</span>
              </p>
              <p className="text-xs text-zinc-400">Clique para fazer o upload</p>
            </div>
          </FileInput>

          <FileUploaderContent className="flex items-center flex-row flex-wrap gap-2">
            {files.map((file, index) => (
              <SortableItem key={file.id} file={file} index={index} onRemove={handleRemoveFile} />
            ))}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={uploadedFiles.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-wrap gap-2">
                  {uploadedFiles.map((file, index) => (
                    <SortableItem
                      key={file.id}
                      file={file}
                      index={index}
                      onRemove={handleRemoveFile}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </FileUploaderContent>
        </FileUploader>
      </FieldControl>

      <Dialog open={!!fileToDelete} onOpenChange={() => setFileToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
            <DialogDescription>{t('common.confirmDeleteFile')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFileToDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              {t('common.remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FieldMessage />
    </Field>
  )
}
